import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase.js';
import { API_BASE_URL } from '../../lib/api.js';
import { useAuth } from '../auth/auth-context.tsx';
import { AppLoading } from '../app-loading.tsx';

interface GymView {
  id: string;
  name: string;
  slug: string;
}

interface GymStats {
  activeRoutes: number;
  activeModifiers: number;
  activeParties: number;
}

function WheelGraphic() {
  // 8 wedges in the hold palette. Rendered as a radial gradient — the SVG
  // rotates, selling the "spin" metaphor without reading as busy.
  const colors = ['#ef4444', '#f97a4d', '#fbbf24', '#4ade80', '#60a5fa', '#c084fc', '#f472b6', '#f5f5f5'];
  const cx = 100;
  const cy = 100;
  const r = 96;
  const seg = (2 * Math.PI) / colors.length;
  const slices = colors.map((c, i) => {
    const a0 = -Math.PI / 2 + i * seg;
    const a1 = a0 + seg;
    const x0 = cx + r * Math.cos(a0);
    const y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1);
    const y1 = cy + r * Math.sin(a1);
    return (
      <path
        key={i}
        d={`M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 0 1 ${x1} ${y1} Z`}
        fill={c}
      />
    );
  });
  return (
    <div className="gym-wheel" aria-hidden="true">
      <svg className="gym-wheel-svg" viewBox="0 0 200 200">
        <circle cx={cx} cy={cy} r={r + 2} fill="#0b0f17" />
        {slices}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#0b0f17" strokeWidth={3} />
      </svg>
      <div className="gym-wheel-tick" />
      <div className="gym-wheel-core">Spin</div>
    </div>
  );
}

export function GymLanding() {
  const { gymSlug } = useParams();
  const [params] = useSearchParams();
  const joinCode = params.get('code');
  const { session } = useAuth();
  const [gym, setGym] = useState<GymView | null>(null);
  const [stats, setStats] = useState<GymStats>({ activeRoutes: 0, activeModifiers: 0, activeParties: 0 });
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!gymSlug) return;
    let cancelled = false;

    async function loadGym() {
      const { data } = await supabase
        .from('gyms')
        .select('id, name, slug')
        .eq('slug', gymSlug)
        .maybeSingle();
      if (cancelled) return;
      if (!data) { setNotFound(true); return; }
      setGym(data);
    }

    // Stats are computed server-side (service role) so they aren't subject to
    // RLS on `parties` / `party_members` — the anon client returns empty for
    // those, which is why the active-party counter previously stuck at 0.
    // Poll every 15s so a freshly created party shows up without a refresh.
    async function loadStats() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/gym/${gymSlug}/stats`);
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as GymStats;
        if (!cancelled) setStats(data);
      } catch {
        /* best-effort — leave previous stats in place */
      }
    }

    void loadGym();
    void loadStats();
    const timer = setInterval(() => void loadStats(), 15_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [gymSlug]);

  if (notFound) {
    return (
      <div className="gym-lander">
        <div className="gym-lander-inner">
          <div className="card">
            <h2>Gym not found</h2>
            <p style={{ color: 'var(--fg-dim)' }}>Double-check your QR code or ask the front desk.</p>
          </div>
        </div>
      </div>
    );
  }

  // This is the page every printed QR code lands on, so it's the one
  // most likely to be waiting on slow gym wifi. Reuse the boot splash's
  // exact visual: the climber sees one continuous loading screen from the
  // moment the HTML arrives to the moment the gym appears, instead of the
  // splash being replaced by a different-looking one.
  if (!gym) return <AppLoading />;

  const enoughRoutes = stats.activeRoutes >= 3;

  return (
    <div className="gym-lander">
      <div className="gym-lander-inner">
        <header className="gym-lander-header">
          <Link to="/" className="gym-lander-brand">
            <span className="dot" />
            Boulder Bash
          </Link>
          <div className="row" style={{ gap: '0.85rem', alignItems: 'center' }}>
            <Link to={`/g/${gym.slug}/leaderboard`} style={{ color: 'var(--fg-dim)', fontSize: '0.9rem' }}>
              Today's board →
            </Link>
            {session ? (
              <Link to="/profile" style={{ color: 'var(--fg-dim)', fontSize: '0.9rem' }}>
                Profile →
              </Link>
            ) : (
              <Link to={`/signup?next=/g/${gym.slug}`} style={{ color: 'var(--fg-dim)', fontSize: '0.9rem' }}>
                Sign up
              </Link>
            )}
          </div>
        </header>

        {joinCode ? (
          <div className="gym-lander-hero" style={{ gridTemplateColumns: '1fr', paddingTop: '1.5rem' }}>
            <div>
              <span className="gym-lander-eyebrow">Welcome to {gym.name}</span>
              <h1 className="gym-lander-name">You were invited.</h1>
              <div className="gym-lander-join">
                <div style={{ color: 'var(--fg-dim)', fontSize: '0.85rem', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                  Join code
                </div>
                <div className="code">{joinCode}</div>
                <Link to={`/g/${gym.slug}/party/join?code=${joinCode}`}>
                  <button className="btn-primary-xl">Join the party →</button>
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="gym-lander-hero">
              <div>
                <span className="gym-lander-eyebrow">Welcome to {gym.name}</span>
                <h1 className="gym-lander-name">{gym.name}</h1>
                <p className="gym-lander-sub">
                  Grab your crew, <strong>spin the wheel</strong>, and let fate pick your next problem.
                  Score points for sends, earn bonuses on challenges, chase today's board.
                </p>
                <div className="gym-lander-cta">
                  <Link to={`/g/${gym.slug}/party/new`}>
                    <button className="btn-primary-xl" disabled={!enoughRoutes}>
                      {enoughRoutes ? 'Start a party' : 'Setup in progress…'}
                    </button>
                  </Link>
                  <Link to={`/g/${gym.slug}/party/join`}>
                    <button className="ghost btn-ghost-xl">Join with a code</button>
                  </Link>
                </div>
                {!enoughRoutes && (
                  <div style={{ color: 'var(--fg-dim)', fontSize: '0.85rem', marginTop: '0.75rem' }}>
                    Gym needs at least 3 active routes before the wheel can spin — ask a setter.
                  </div>
                )}
              </div>
              <WheelGraphic />
            </div>

            <div className="gym-lander-strip">
              <div className="cell">
                <div className="k">Routes</div>
                <div className="v">{stats.activeRoutes} up</div>
              </div>
              <div className="cell">
                <div className="k">Challenges</div>
                <div className="v">{stats.activeModifiers} in the pool</div>
              </div>
              <div className="cell">
                <div className="k">Parties right now</div>
                <div className="v">{stats.activeParties}</div>
              </div>
            </div>
          </>
        )}

        <div className="gym-lander-foot">
          <div>© Boulder Bash · /{gym.slug}</div>
          <div className="row" style={{ gap: '1rem' }}>
            <Link to={`/g/${gym.slug}/leaderboard`}>Daily leaderboard</Link>
            <Link to="/login" style={{ color: 'var(--fg-dim)' }}>Gym staff</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
